import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, ShieldCheck, Clock, ChevronLeft, ChevronRight, Plus, Trash2, ClipboardCheck, Upload, Link, FileText, CheckCircle, AlertCircle, Clock3, FileDown } from "lucide-react";
import AdminLayout, { AdminPageHead } from "./AdminLayout";
import { adminNavItem } from "./AdminNav";
import { fetchAdminContent, changeUserRole, fetchReviewerWhitelist, addReviewerToWhitelist, removeReviewerFromWhitelist, fetchShowcasePapers, removeShowcasePaper, fetchShowcasePresignedUrl, insertShowcasePaper, downloadShowcaseCsv, downloadUsersCsv } from "../service/UserService";
import { fetchAdminFeatureFlags, setFeatureFlag } from "../service/FeatureFlagService";
import { fetchSchedulerSetting, setSchedulerEnabled } from "../service/SchedulerService";
import { getJwtRole } from "../service/AuthService";
import { admin } from "../constants/Constants";

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

function fmt(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
}

// Coarse "time from now" label for an auto-enable countdown (e.g. "in 47 min", "in 2 hr").
function fmtRelative(ts) {
    const diff = ts - Date.now();
    if (diff <= 0) return "shortly";
    const mins = Math.round(diff / 60000);
    if (mins < 60) return `in ${mins} min`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem ? `in ${hrs} hr ${rem} min` : `in ${hrs} hr`;
}

// Small pill button shared by the per-section auto-enable timer controls.
const TIMER_BTN = {
    fontSize: 11.5, fontWeight: 500, color: "var(--series-blue)",
    background: "var(--info-bg)", border: "1px solid var(--info-border-soft)", borderRadius: 6,
    padding: "2px 8px", cursor: "pointer", lineHeight: 1.6,
};

// The sliding on/off switch on the Sections page (the scheduler and every section row).
function ToggleSwitch({ on, busy, onClick, label }) {
    return (
        <button
            disabled={busy}
            onClick={onClick}
            style={{
                position: "relative", display: "inline-flex",
                width: 40, height: 22, borderRadius: 11,
                border: "none", cursor: busy ? "not-allowed" : "pointer",
                background: on ? "#22c55e" : "var(--toggle-off-cool)",
                transition: "background 0.2s", flexShrink: 0,
                opacity: busy ? 0.6 : 1,
            }}
            aria-label={label}
        >
            <span style={{
                position: "absolute", top: 3,
                left: on ? 21 : 3,
                width: 16, height: 16, borderRadius: "50%",
                background: "white", transition: "left 0.2s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
            }} />
        </button>
    );
}

export default function Admin({ section = "users" }) {
    const navigate = useNavigate();
    const nav = adminNavItem(section);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [lastRun, setLastRun] = useState(null);
    const [search, setSearch] = useState("");
    const [togglingEmail, setTogglingEmail] = useState(null);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 5;
    const [exportingUsers, setExportingUsers] = useState(false);
    const [usersExportError, setUsersExportError] = useState("");

    // Reviewer whitelist state
    const [reviewers, setReviewers] = useState([]);
    const [reviewersLoading, setReviewersLoading] = useState(true);
    const [showAddReviewer, setShowAddReviewer] = useState(false);
    const [newReviewer, setNewReviewer] = useState({ email: "", name: "", institution: "", specialty: "" });
    const [addingReviewer, setAddingReviewer] = useState(false);
    const [addReviewerError, setAddReviewerError] = useState("");
    const [removingId, setRemovingId] = useState(null);

    // Showcase state
    const [showcasePapers, setShowcasePapers] = useState([]);
    const [showcaseLoading, setShowcaseLoading] = useState(true);
    const [showUploadShowcase, setShowUploadShowcase] = useState(false);
    const [showcaseFile, setShowcaseFile] = useState(null);
    const [showcaseTitle, setShowcaseTitle] = useState("");
    const [showcaseAuthors, setShowcaseAuthors] = useState("");
    const [uploadingShowcase, setUploadingShowcase] = useState(false);
    const [showcaseError, setShowcaseError] = useState("");
    const [removingShowcaseId, setRemovingShowcaseId] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);
    const [showcasePage, setShowcasePage] = useState(1);
    // Showcase CSV export popup (mirrors the outreach export).
    const [scExportModal, setScExportModal] = useState(false);
    const [scExporting, setScExporting] = useState(false);
    const [scExportStart, setScExportStart] = useState("");
    const [scExportEnd, setScExportEnd] = useState("");
    // Which date the range filters on: "uploaded" (default) or "scored".
    const [scExportDateField, setScExportDateField] = useState("uploaded");
    const SHOWCASE_PAGE_SIZE = 5;

    // Section availability (feature flags)
    const [featureFlags, setFeatureFlags] = useState([]);
    const [flagsLoading, setFlagsLoading] = useState(true);
    const [togglingFlag, setTogglingFlag] = useState(null);
    // Per-section auto-enable timer: which row's "Custom…" picker is open, and its value.
    const [customTimerKey, setCustomTimerKey] = useState(null);
    const [customTimerValue, setCustomTimerValue] = useState("");

    // Scheduler switch (Sections page): the full { enabled, updatedAt, updatedBy } row.
    const [scheduler, setScheduler] = useState(null);
    const [schedulerLoading, setSchedulerLoading] = useState(true);
    const [togglingScheduler, setTogglingScheduler] = useState(false);
    // Users page: whether the scheduler is on, so the Last Run card can say why that time has stopped moving.
    const [schedulerOn, setSchedulerOn] = useState(true);
    // Bumped on each toggle so a poll response that left before it can't overwrite the new state.
    const schedulerToggleSeq = useRef(0);

    // Each section is its own route, so only fetch what the visible one needs.
    useEffect(() => {
        if (getJwtRole() !== admin) {
            navigate("/");
            return;
        }
        if (section === "users") {
            fetchAdminContent(true)
                .then(data => {
                    setUsers(data.users ?? []);
                    setLastRun(data.lastRun ?? null);
                    setSchedulerOn(data.schedulerEnabled !== false);
                    setLoading(false);
                })
                .catch(() => {
                    navigate("/login");
                });
        }
        if (section === "reviewers") {
            fetchReviewerWhitelist()
                .then(data => setReviewers(data.reviewers ?? []))
                .catch(() => {})
                .finally(() => setReviewersLoading(false));
        }
        if (section === "showcase") {
            fetchShowcasePapers()
                .then(data => setShowcasePapers((data.manuscripts ?? []).sort((a, b) => (b.uploadDate ?? 0) - (a.uploadDate ?? 0))))
                .catch(() => {})
                .finally(() => setShowcaseLoading(false));
        }
        if (section === "sections") {
            loadFeatureFlags().finally(() => setFlagsLoading(false));
            loadScheduler().finally(() => setSchedulerLoading(false));
        }
    }, [section]);

    // Keep the panel honest while it's open: re-fetch every 30s so countdowns tick down,
    // any timer that fired (auto-re-enable) shows up, and another admin's scheduler
    // toggle appears without a manual refresh.
    useEffect(() => {
        if (section !== "sections") return;
        const id = setInterval(() => { loadFeatureFlags(); loadScheduler(); }, 30000);
        return () => clearInterval(id);
    }, [section]);

    // The Users and Sections routes share this component instance, so every scheduler
    // update also refreshes schedulerOn for the Last Run card.
    function loadScheduler() {
        const seq = schedulerToggleSeq.current;
        return fetchSchedulerSetting()
            .then(data => {
                if (!data.scheduler || seq !== schedulerToggleSeq.current) return;
                setScheduler(data.scheduler);
                setSchedulerOn(data.scheduler.enabled !== false);
            })
            .catch(() => {});
    }

    function handleToggleScheduler() {
        const newEnabled = !scheduler.enabled;
        schedulerToggleSeq.current++;
        setTogglingScheduler(true);
        setScheduler(prev => ({ ...prev, enabled: newEnabled }));
        const fail = () => {
            setScheduler(prev => ({ ...prev, enabled: !newEnabled }));
            loadScheduler();
            alert("Could not update the scheduler.");
        };
        setSchedulerEnabled(newEnabled)
            .then(res => {
                if (res.success && res.scheduler) {
                    setScheduler(res.scheduler);
                    setSchedulerOn(res.scheduler.enabled !== false);
                } else {
                    fail();
                }
            })
            .catch(fail)
            .finally(() => setTogglingScheduler(false));
    }

    function loadFeatureFlags() {
        return fetchAdminFeatureFlags()
            .then(data => setFeatureFlags(data.flags ?? []))
            .catch(() => {});
    }

    function handleToggleFlag(key, currentEnabled) {
        const newEnabled = !currentEnabled;
        setTogglingFlag(key);
        // Optimistic update; revert on failure. The switch is the indefinite on/off, so any
        // pending auto-enable timer is cleared (enabling or disabling-without-a-timer).
        setFeatureFlags(prev => prev.map(f => f.key === key ? { ...f, enabled: newEnabled, autoEnableAt: null } : f));
        setFeatureFlag(key, newEnabled)
            .then(res => {
                if (res.success && res.flag) {
                    setFeatureFlags(prev => prev.map(f => f.key === key ? res.flag : f));
                } else {
                    loadFeatureFlags();
                    alert("Could not update section availability.");
                }
            })
            .catch(() => {
                loadFeatureFlags();
                alert("Could not update section availability.");
            })
            .finally(() => setTogglingFlag(null));
    }

    // Arm or clear (autoEnableAt = null) the auto-enable timer on an already-disabled section.
    function handleSetTimer(key, autoEnableAt) {
        setTogglingFlag(key);
        setCustomTimerKey(null);
        setFeatureFlags(prev => prev.map(f => f.key === key ? { ...f, enabled: false, autoEnableAt } : f));
        setFeatureFlag(key, false, autoEnableAt)
            .then(res => {
                if (res.success && res.flag) {
                    setFeatureFlags(prev => prev.map(f => f.key === key ? res.flag : f));
                } else {
                    loadFeatureFlags();
                    alert("Could not update the auto-enable timer.");
                }
            })
            .catch(() => { loadFeatureFlags(); alert("Could not update the auto-enable timer."); })
            .finally(() => setTogglingFlag(null));
    }

    function applyCustomTimer(key) {
        const ts = new Date(customTimerValue).getTime();
        if (!ts || ts <= Date.now()) {
            alert("Pick a date and time in the future.");
            return;
        }
        setCustomTimerValue("");
        handleSetTimer(key, ts);
    }

    function handleRoleChange(email, newRole) {
        setTogglingEmail(email);
        changeUserRole(email, newRole, true)
            .then(res => {
                if (res.success) {
                    setUsers(prev => prev.map(u =>
                        u.email === email ? { ...u, role: newRole } : u
                    ));
                } else {
                    alert("Could not change user role.");
                }
                setTogglingEmail(null);
            })
            .catch(() => {
                alert("Could not change user role.");
                setTogglingEmail(null);
            });
    }

    const handleAddReviewer = async () => {
        if (!newReviewer.email) { setAddReviewerError("Email is required."); return; }
        setAddingReviewer(true);
        setAddReviewerError("");
        try {
            const res = await addReviewerToWhitelist(newReviewer);
            if (res.success) {
                setReviewers(prev => [...prev, res.reviewer]);
                setNewReviewer({ email: "", name: "", institution: "", specialty: "" });
                setShowAddReviewer(false);
            } else {
                setAddReviewerError(res.message || "Failed to add reviewer.");
            }
        } catch {
            setAddReviewerError("Failed to add reviewer.");
        } finally {
            setAddingReviewer(false);
        }
    };

    const handleRemoveReviewer = async (id) => {
        if (!window.confirm("Remove this reviewer from the whitelist?")) return;
        setRemovingId(id);
        try {
            const res = await removeReviewerFromWhitelist(id);
            if (res.success) {
                setReviewers(prev => prev.filter(r => r.id !== id));
            }
        } catch {}
        finally { setRemovingId(null); }
    };

    const handleUploadShowcase = async () => {
        if (!showcaseFile) { setShowcaseError("Please select a PDF file."); return; }
        if (showcaseFile.type !== "application/pdf") { setShowcaseError("File must be a PDF."); return; }
        setUploadingShowcase(true);
        setShowcaseError("");
        try {
            // 1. Get presigned URL
            const presignedRes = await fetchShowcasePresignedUrl();
            if (!presignedRes?.presignedUrl?.url) throw new Error("Failed to get upload URL");

            // 2. Upload directly to S3
            const uploadResp = await fetch(presignedRes.presignedUrl.url, {
                method: "PUT",
                headers: { "Content-Type": "application/pdf" },
                body: showcaseFile,
            });
            if (!uploadResp.ok) throw new Error("S3 upload failed");

            // 3. Insert manuscript record
            const res = await insertShowcasePaper(
                presignedRes.presignedUrl.key,
                showcaseFile.name,
                showcaseTitle || null,
                showcaseAuthors || null
            );
            if (res.success) {
                setShowcasePapers(prev => [res.manuscript, ...prev]);
                setShowcaseFile(null);
                setShowcaseTitle("");
                setShowcaseAuthors("");
                setShowUploadShowcase(false);
            } else {
                setShowcaseError(res.message || "Upload failed.");
            }
        } catch (e) {
            setShowcaseError(e.message || "Upload failed.");
        } finally {
            setUploadingShowcase(false);
        }
    };

    const handleRemoveShowcase = async (id) => {
        if (!window.confirm("Remove showcase status? The link will stop working.")) return;
        setRemovingShowcaseId(id);
        try {
            const res = await removeShowcasePaper(id);
            if (res.success) {
                setShowcasePapers(prev => prev.filter(m => m.id !== id));
            }
        } catch {}
        finally { setRemovingShowcaseId(null); }
    };

    const openShowcaseExport = () => {
        setScExportStart("");
        setScExportEnd("");
        setScExportDateField("uploaded");
        setShowcaseError("");
        setScExportModal(true);
    };

    const handleShowcaseExport = async (startDate, endDate, dateField) => {
        setScExporting(true);
        setShowcaseError("");
        try {
            const { blob, disposition } = await downloadShowcaseCsv(startDate || "", endDate || "", dateField || "uploaded");
            const match = disposition && disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : "showcase_papers.csv";
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
            setScExportModal(false);
        } catch {
            setShowcaseError("There was an error exporting the CSV. Please try again.");
        } finally {
            setScExporting(false);
        }
    };

    const handleUsersExport = async () => {
        setExportingUsers(true);
        setUsersExportError("");
        try {
            const { blob, disposition } = await downloadUsersCsv();
            const match = disposition && disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : "users.csv";
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            setUsersExportError("There was an error exporting the CSV. Please try again.");
        } finally {
            setExportingUsers(false);
        }
    };

    const copyShowcaseLink = (key) => {
        const link = window.location.origin + "/showcase/" + key;
        navigator.clipboard.writeText(link);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const filtered = users
        .filter(u => {
            const q = search.toLowerCase();
            return (
                (u.firstName ?? "").toLowerCase().includes(q) ||
                (u.lastName ?? "").toLowerCase().includes(q) ||
                (u.email ?? "").toLowerCase().includes(q)
            );
        })
        .sort((a, b) => (a.dateCreated ?? 0) - (b.dateCreated ?? 0));

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const adminCount = users.filter(u => u.role === "ADMIN").length;

    const overlays = (
        <>
            {/* Add Reviewer Modal */}
            {showAddReviewer && (
                <div className="share-modal-overlay admin-modal-center" onClick={() => { if (!addingReviewer) setShowAddReviewer(false); }}>
                    <div className="share-modal-tile" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Add Reviewer</p>
                            <button className="share-modal-close" onClick={() => { if (!addingReviewer) setShowAddReviewer(false); }}>x</button>
                        </div>
                        <div className="share-modal-body">
                            {addReviewerError && <p className="chpw-error" style={{ marginBottom: 12 }}>{addReviewerError}</p>}
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                <div>
                                    <label className="upload-label">Email *</label>
                                    <input className="upload-input" type="email" placeholder="reviewer@university.edu" value={newReviewer.email} onChange={e => setNewReviewer(s => ({ ...s, email: e.target.value }))} disabled={addingReviewer} />
                                </div>
                                <div>
                                    <label className="upload-label">Name</label>
                                    <input className="upload-input" type="text" placeholder="Dr. Jane Smith" value={newReviewer.name} onChange={e => setNewReviewer(s => ({ ...s, name: e.target.value }))} disabled={addingReviewer} />
                                </div>
                                <div>
                                    <label className="upload-label">Institution</label>
                                    <input className="upload-input" type="text" placeholder="University of..." value={newReviewer.institution} onChange={e => setNewReviewer(s => ({ ...s, institution: e.target.value }))} disabled={addingReviewer} />
                                </div>
                                <div>
                                    <label className="upload-label">Specialty</label>
                                    <input className="upload-input" type="text" placeholder="Virology, Microbiology..." value={newReviewer.specialty} onChange={e => setNewReviewer(s => ({ ...s, specialty: e.target.value }))} disabled={addingReviewer} />
                                </div>
                            </div>
                            <button className="auth-submit-btn" style={{ marginTop: 14 }} onClick={handleAddReviewer} disabled={addingReviewer}>
                                {addingReviewer ? "Adding..." : "Add Reviewer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Showcase Modal */}
            {showUploadShowcase && (
                <div className="share-modal-overlay admin-modal-center" onClick={() => { if (!uploadingShowcase) setShowUploadShowcase(false); }}>
                    <div className="share-modal-tile" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Upload Showcase Paper</p>
                            <button className="share-modal-close" onClick={() => { if (!uploadingShowcase) setShowUploadShowcase(false); }}>x</button>
                        </div>
                        <div className="share-modal-body">
                            {showcaseError && <p className="chpw-error" style={{ marginBottom: 12 }}>{showcaseError}</p>}
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                <div>
                                    <label className="upload-label">PDF File *</label>
                                    <div
                                        onClick={() => !uploadingShowcase && document.getElementById("showcase-file-input").click()}
                                        style={{
                                            border: "2px dashed " + (showcaseFile ? "#5ecfa0" : "var(--border)"),
                                            borderRadius: 10,
                                            padding: "24px 16px",
                                            textAlign: "center",
                                            cursor: uploadingShowcase ? "default" : "pointer",
                                            background: showcaseFile ? "var(--accent-soft)" : "var(--surface-2)",
                                            transition: "border-color 0.15s, background 0.15s",
                                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                        }}
                                    >
                                        {showcaseFile ? (
                                            <>
                                                <FileText size={20} color="var(--brand-text)" />
                                                <p style={{ margin: "6px 0 0", fontSize: 13, fontWeight: 500, color: "var(--ink-900)" }}>{showcaseFile.name}</p>
                                                <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--ink-400)" }}>Click to change</p>
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={20} color="var(--ink-300)" />
                                                <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--ink-500)" }}>Drop PDF here or <u>browse</u></p>
                                                <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--ink-300)" }}>Max 50 MB</p>
                                            </>
                                        )}
                                    </div>
                                    <input
                                        id="showcase-file-input"
                                        type="file"
                                        accept="application/pdf"
                                        onChange={e => setShowcaseFile(e.target.files[0] || null)}
                                        disabled={uploadingShowcase}
                                        style={{ display: "none" }}
                                    />
                                </div>
                                <div>
                                    <label className="upload-label" style={{ display: "block", marginBottom: 4 }}>Title</label>
                                    <input className="upload-input" style={{ width: "100%", boxSizing: "border-box" }} type="text" placeholder="Manuscript title" value={showcaseTitle} onChange={e => setShowcaseTitle(e.target.value)} disabled={uploadingShowcase} />
                                </div>
                                <div>
                                    <label className="upload-label" style={{ display: "block", marginBottom: 4 }}>Authors</label>
                                    <input className="upload-input" style={{ width: "100%", boxSizing: "border-box" }} type="text" placeholder="Author names" value={showcaseAuthors} onChange={e => setShowcaseAuthors(e.target.value)} disabled={uploadingShowcase} />
                                </div>
                            </div>
                            <p style={{ fontSize: 12, color: "var(--ink-400)", margin: "12px 0 0", lineHeight: 1.5 }}>
                                The paper will go through normal scoring. Once complete, you can copy the shareable link.
                            </p>
                            <button className="auth-submit-btn" style={{ marginTop: 14 }} onClick={handleUploadShowcase} disabled={uploadingShowcase}>
                                {uploadingShowcase ? "Uploading..." : "Upload & Create Showcase"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Showcase Export CSV modal */}
            {scExportModal && (
                <div className="share-modal-overlay admin-modal-center" onClick={() => { if (!scExporting) setScExportModal(false); }}>
                    <div className="share-modal-tile" onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Export CSV</p>
                            <button className="share-modal-close" onClick={() => { if (!scExporting) setScExportModal(false); }}>×</button>
                        </div>
                        <div className="share-modal-body">
                            <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "var(--ink-500)", lineHeight: 1.5 }}>
                                Exports every showcase paper with its title, authors, PDF filename, public showcase link,
                                status, and dates. Filter by a date range, or leave both dates blank to export everything.
                            </p>
                            {showcaseError && <p className="chpw-error" style={{ marginBottom: 12 }}>{showcaseError}</p>}
                            <div style={{ display: "grid", gap: 6 }}>
                                <label style={{ fontSize: 11, color: "var(--ink-400)" }}>Start date <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(optional)</span></label>
                                <input type="date" value={scExportStart} onChange={e => setScExportStart(e.target.value)} className="sub-date-input" />
                                <label style={{ fontSize: 11, color: "var(--ink-400)", marginTop: 4 }}>End date <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(optional)</span></label>
                                <input type="date" value={scExportEnd} onChange={e => setScExportEnd(e.target.value)} className="sub-date-input" />
                            </div>
                            <div style={{ display: "grid", gap: 6, marginTop: 12 }}>
                                <label style={{ fontSize: 11, color: "var(--ink-400)" }}>Filter dates by</label>
                                <label style={{ fontSize: 12.5, color: "var(--ink-750)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                    <input type="radio" name="scExportDateField" value="uploaded"
                                        checked={scExportDateField === "uploaded"}
                                        onChange={() => setScExportDateField("uploaded")} />
                                    Date the paper was uploaded
                                </label>
                                <label style={{ fontSize: 12.5, color: "var(--ink-750)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                    <input type="radio" name="scExportDateField" value="scored"
                                        checked={scExportDateField === "scored"}
                                        onChange={() => setScExportDateField("scored")} />
                                    Date it finished scoring
                                </label>
                                {scExportDateField === "scored" && (
                                    <p style={{ margin: "2px 0 0", fontSize: 11.5, color: "var(--bronze)", lineHeight: 1.45 }}>
                                        Papers that haven't finished scoring have no scored date, so they'll be left out of a filtered export.
                                    </p>
                                )}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                                <button
                                    className="sub-date-clear"
                                    style={{ flex: 1, padding: "10px", opacity: scExporting ? 0.6 : 1, cursor: scExporting ? "default" : "pointer" }}
                                    disabled={scExporting}
                                    onClick={() => handleShowcaseExport("", "", scExportDateField)}
                                >
                                    Export all
                                </button>
                                {(() => {
                                    // The backend only filters when BOTH dates are present and
                                    // start <= end; anything else silently exports everything.
                                    // Block that here rather than handing back a surprise full dump.
                                    const partial = (!!scExportStart) !== (!!scExportEnd);
                                    const inverted = scExportStart && scExportEnd && scExportStart > scExportEnd;
                                    const blocked = scExporting || partial || inverted;
                                    return (
                                        <button
                                            className="auth-submit-btn"
                                            style={{ flex: 1, marginTop: 0, opacity: blocked ? 0.6 : 1, cursor: blocked ? "default" : "pointer" }}
                                            disabled={blocked}
                                            title={partial ? "Set both dates, or use Export all" : (inverted ? "Start date must be on or before the end date" : "")}
                                            onClick={() => handleShowcaseExport(scExportStart, scExportEnd, scExportDateField)}
                                        >
                                            {scExporting ? "Exporting..." : "Export"}
                                        </button>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <AdminLayout overlays={overlays}>
                    <AdminPageHead title={nav.title} subtitle={nav.subtitle} />

                    {/* Stats row */}
                    {section === "users" && (
                    <div className="admin-stats-row">
                        <div className="submissions-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{ width: 38, height: 38, borderRadius: 10, background: "var(--blue-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Users size={18} color="var(--blue)" />
                            </div>
                            <div>
                                <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1.1 }}>
                                    {loading ? "—" : users.length}
                                </p>
                                <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 2 }}>Total Users</p>
                            </div>
                        </div>
                        <div className="submissions-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{ width: 38, height: 38, borderRadius: 10, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <ShieldCheck size={18} color="#22c55e" />
                            </div>
                            <div>
                                <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1.1 }}>
                                    {loading ? "—" : adminCount}
                                </p>
                                <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 2 }}>Admins</p>
                            </div>
                        </div>
                        <div className="submissions-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{ width: 38, height: 38, borderRadius: 10, background: "var(--warm-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Clock size={18} color="#f59e0b" />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--ink-900)", lineHeight: 1.2 }}>
                                    {loading ? "—" : fmtDateTime(lastRun)}
                                </p>
                                <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 2 }}>
                                    Last Run
                                    {!loading && !schedulerOn && (
                                        <span style={{ color: "var(--danger-muted)", fontWeight: 600 }}> · Scheduler off</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Scheduler switch */}
                    {section === "sections" && (
                    <div className="submissions-card" style={{ marginBottom: 16 }}>
                        <div style={{ padding: "4px 20px 6px" }}>
                            <p style={{ margin: "8px 0 4px", fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-300)" }}>
                                Background Processing
                            </p>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ fontSize: 13.5, fontWeight: 500, color: "var(--ink-900)" }}>Scheduler</span>
                                    <p style={{ margin: "2px 0 0", fontSize: 11.5, color: "var(--ink-400)" }}>
                                        Scores submitted papers in the background. While it's off, runs are skipped and new papers wait in the queue.
                                    </p>
                                    {scheduler?.updatedBy && scheduler.updatedBy !== "system" && (
                                        <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--ink-300)" }}>
                                            Changed by {scheduler.updatedBy} · {fmtDateTime(scheduler.updatedAt)}
                                        </p>
                                    )}
                                </div>
                                {schedulerLoading ? (
                                    <span style={{ fontSize: 12, color: "var(--ink-300)" }}>Loading…</span>
                                ) : !scheduler ? (
                                    <span style={{ fontSize: 12, color: "var(--ink-300)" }}>Unavailable</span>
                                ) : (
                                    <>
                                        {!scheduler.enabled && (
                                            <span className="status-badge badge-error" style={{ whiteSpace: "nowrap" }}>
                                                <AlertCircle size={12} />
                                                Paused
                                            </span>
                                        )}
                                        <ToggleSwitch
                                            on={scheduler.enabled}
                                            busy={togglingScheduler}
                                            onClick={handleToggleScheduler}
                                            label={scheduler.enabled ? "Disable scheduler" : "Enable scheduler"}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Section Availability (feature flags) */}
                    {section === "sections" && (
                    <div className="submissions-card">
                        {flagsLoading ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>Loading…</div>
                        ) : featureFlags.length === 0 ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>No sections found.</div>
                        ) : (
                            <div style={{ padding: "4px 20px 16px" }}>
                                {featureFlags.map((f, i) => {
                                    const showHeader = i === 0 || featureFlags[i - 1].category !== f.category;
                                    return (
                                        <div key={f.key}>
                                            {showHeader && (
                                                <p style={{ margin: i === 0 ? "8px 0 4px" : "16px 0 4px", fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-300)" }}>
                                                    {f.category}
                                                </p>
                                            )}
                                            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: i === 0 || showHeader ? "none" : "1px solid var(--border-subtle)" }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <span style={{ fontSize: 13.5, fontWeight: 500, color: "var(--ink-900)" }}>{f.label}</span>
                                                    {f.updatedBy && f.updatedBy !== "system" && (
                                                        <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--ink-300)" }}>
                                                            Changed by {f.updatedBy} · {fmtDateTime(f.updatedAt)}
                                                        </p>
                                                    )}
                                                </div>
                                                {!f.enabled && (
                                                    <span className="status-badge badge-error" style={{ whiteSpace: "nowrap" }}>
                                                        <AlertCircle size={12} />
                                                        Under Construction
                                                    </span>
                                                )}
                                                <ToggleSwitch
                                                    on={f.enabled}
                                                    busy={togglingFlag === f.key}
                                                    onClick={() => handleToggleFlag(f.key, f.enabled)}
                                                    label={f.enabled ? `Disable ${f.label}` : `Enable ${f.label}`}
                                                />
                                            </div>
                                            {!f.enabled && (
                                                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, padding: "0 0 10px" }}>
                                                    <Clock3 size={12} color="var(--ink-350)" style={{ flexShrink: 0 }} />
                                                    {f.autoEnableAt ? (
                                                        <>
                                                            <span style={{ fontSize: 11.5, color: "var(--ink-450)" }}>
                                                                Auto-enables {fmtRelative(f.autoEnableAt)} · {fmtDateTime(f.autoEnableAt)}
                                                            </span>
                                                            <button disabled={togglingFlag === f.key} onClick={() => handleSetTimer(f.key, null)}
                                                                style={{ ...TIMER_BTN, color: "var(--danger-muted)", background: "var(--danger-muted-bg)", borderColor: "var(--danger-muted-border)" }}>
                                                                clear
                                                            </button>
                                                        </>
                                                    ) : customTimerKey === f.key ? (
                                                        <>
                                                            <input type="datetime-local" value={customTimerValue} autoFocus
                                                                onChange={(e) => setCustomTimerValue(e.target.value)}
                                                                style={{ fontSize: 12, padding: "3px 6px", border: "1px solid var(--border)", borderRadius: 6, color: "var(--ink-900)" }} />
                                                            <button disabled={!customTimerValue || togglingFlag === f.key} onClick={() => applyCustomTimer(f.key)} style={TIMER_BTN}>
                                                                Set
                                                            </button>
                                                            <button onClick={() => setCustomTimerKey(null)}
                                                                style={{ ...TIMER_BTN, color: "var(--ink-350)", background: "var(--surface-3)", borderColor: "var(--border)" }}>
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span style={{ fontSize: 11.5, color: "var(--ink-350)" }}>Auto-enable in</span>
                                                            {[{ label: "1h", h: 1 }, { label: "2h", h: 2 }, { label: "4h", h: 4 }].map(p => (
                                                                <button key={p.label} disabled={togglingFlag === f.key}
                                                                    onClick={() => handleSetTimer(f.key, Date.now() + p.h * 3600000)} style={TIMER_BTN}>
                                                                    {p.label}
                                                                </button>
                                                            ))}
                                                            <button disabled={togglingFlag === f.key}
                                                                onClick={() => { setCustomTimerKey(f.key); setCustomTimerValue(""); }} style={TIMER_BTN}>
                                                                Custom…
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    )}

                    {/* Users table */}
                    {section === "users" && (
                    <div className="submissions-card">
                        <div className="admin-users-header">
                            <p style={{ margin: 0, fontSize: 13, color: "var(--ink-400)", flex: 1 }}>
                                {search ? `${filtered.length} matching` : `${users.length} total`}
                            </p>
                            <div className="submissions-search-wrap" style={{ position: "relative" }}>
                                <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                                <input
                                    type="text"
                                    placeholder="Search users…"
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                                    style={{
                                        paddingLeft: 32, paddingRight: search ? 28 : 12,
                                        paddingTop: 7, paddingBottom: 7,
                                        border: "1px solid var(--border)", borderRadius: 8,
                                        fontSize: 13, background: "var(--surface-2)",
                                        outline: "none", width: 200,
                                    }}
                                />
                                {search && (
                                    <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-300)", fontSize: 16, lineHeight: 1 }}>×</button>
                                )}
                            </div>
                            {/* Always the full user list with submission and activity stats, whatever the search box holds */}
                            <button
                                onClick={handleUsersExport}
                                disabled={exportingUsers || loading || users.length === 0}
                                title="Download every account with contact details, submissions, and activity"
                                style={{
                                    display: "flex", alignItems: "center", gap: 5,
                                    padding: "6px 14px", borderRadius: 8,
                                    border: "1px solid var(--border)", background: "var(--surface)",
                                    color: "var(--ink-900)", fontSize: 12, fontWeight: 500,
                                    cursor: (exportingUsers || loading || users.length === 0) ? "default" : "pointer",
                                    opacity: (exportingUsers || loading || users.length === 0) ? 0.6 : 1, whiteSpace: "nowrap",
                                }}
                            >
                                <FileDown size={13} />
                                {exportingUsers ? "Exporting..." : "Export CSV"}
                            </button>
                        </div>
                        {usersExportError && <p className="chpw-error" style={{ margin: "12px 20px 0" }}>{usersExportError}</p>}

                        {loading ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>Loading…</div>
                        ) : filtered.length === 0 ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>No users found.</div>
                        ) : (
                            <table className="submissions-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th className="admin-col-email">Email</th>
                                        <th className="admin-col-joined">Joined</th>
                                        <th className="admin-col-joined">Source</th>
                                        <th>Role</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((u, i) => (
                                        <tr key={i} className="submission-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/user?id=${u.id}`)}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    {u.imageUrl ? (
                                                        <img
                                                            src={u.imageUrl.replaceAll('"', '')}
                                                            alt=""
                                                            style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: 32, height: 32, borderRadius: "50%",
                                                            background: "var(--blue-bg)", color: "var(--blue)",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            fontSize: 12, fontWeight: 600, flexShrink: 0,
                                                        }}>
                                                            {(u.firstName?.[0] ?? "?").toUpperCase()}
                                                        </div>
                                                    )}
                                                    <span style={{ fontWeight: 500, color: "var(--ink-900)", fontSize: 13 }}>
                                                        {u.firstName} {u.lastName}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="admin-col-email" style={{ color: "var(--ink-500)", fontSize: 13 }}>{u.email}</td>
                                            <td className="admin-col-joined" style={{ color: "var(--ink-350)", fontSize: 12 }}>{fmt(u.dateCreated)}</td>
                                            <td className="admin-col-joined" style={{ fontSize: 12 }} title={u.signupSource ? "Where this account signed up from" : "Signed up before source tracking"}>
                                                {u.signupSource ? (
                                                    <span style={{ padding: "2px 8px", borderRadius: 5, background: "var(--blue-bg)", color: "var(--blue)", fontWeight: 500 }}>{u.signupSource}</span>
                                                ) : (
                                                    <span style={{ color: "var(--ink-300)" }}>—</span>
                                                )}
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <select
                                                    value={u.role}
                                                    disabled={togglingEmail === u.email}
                                                    onChange={(e) => handleRoleChange(u.email, e.target.value)}
                                                    aria-label="User role"
                                                    style={{
                                                        padding: "5px 8px", borderRadius: 7,
                                                        border: "1px solid var(--border)", background: "var(--surface-2)",
                                                        color: "var(--ink-700)", fontSize: 12.5,
                                                        cursor: togglingEmail === u.email ? "not-allowed" : "pointer",
                                                        opacity: togglingEmail === u.email ? 0.6 : 1,
                                                    }}
                                                >
                                                    <option value="USER">User</option>
                                                    <option value="FUNDER">Funder</option>
                                                    <option value="ADMIN">Admin</option>
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {!loading && totalPages > 1 && (
                            <div style={{ borderTop: "1px solid var(--border-subtle)", padding: "12px 20px" }}>
                                <div className="pagination-bar">
                                    <p className="pagination-count">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length} users</p>
                                    <div className="pagination-controls">
                                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                                        <div className="pagination-pages">
                                            {pageWindows(safePage, totalPages).map((n, i) =>
                                                n === null
                                                    ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>…</span>
                                                    : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                                            )}
                                        </div>
                                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                                    </div>
                                    <div className="pagination-spacer" />
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Reviewer Whitelist */}
                    {section === "reviewers" && (
                    <div className="submissions-card">
                        <div className="admin-users-header">
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                                <ClipboardCheck size={16} color="var(--series-blue)" />
                                <p style={{ margin: 0, fontSize: 13, color: "var(--ink-400)" }}>{reviewers.length} approved</p>
                            </div>
                            <button
                                onClick={() => { setShowAddReviewer(true); setAddReviewerError(""); }}
                                style={{
                                    display: "flex", alignItems: "center", gap: 5,
                                    padding: "6px 14px", borderRadius: 8,
                                    border: "none", background: "var(--brand)",
                                    color: "white", cursor: "pointer", fontSize: 12, fontWeight: 500,
                                }}
                            >
                                <Plus size={13} />
                                Add Reviewer
                            </button>
                        </div>

                        {reviewersLoading ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>Loading...</div>
                        ) : reviewers.length === 0 ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>No reviewers in whitelist.</div>
                        ) : (
                            <table className="submissions-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th className="admin-col-email">Email</th>
                                        <th className="admin-col-joined">Specialty</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reviewers.map(r => (
                                        <tr key={r.id} className="submission-row">
                                            <td>
                                                <div>
                                                    <span style={{ fontWeight: 500, color: "var(--ink-900)", fontSize: 13 }}>{r.name || "—"}</span>
                                                    {r.institution && <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--ink-400)" }}>{r.institution}</p>}
                                                </div>
                                            </td>
                                            <td className="admin-col-email" style={{ color: "var(--ink-500)", fontSize: 13 }}>{r.email}</td>
                                            <td className="admin-col-joined" style={{ color: "var(--ink-400)", fontSize: 12 }}>{r.specialty || "—"}</td>
                                            <td>
                                                <button
                                                    onClick={() => handleRemoveReviewer(r.id)}
                                                    disabled={removingId === r.id}
                                                    style={{
                                                        background: "none", border: "none", cursor: "pointer",
                                                        color: "var(--ink-200)", padding: 4, display: "flex", alignItems: "center",
                                                    }}
                                                    title="Remove reviewer"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    )}
                    {/* Showcase Papers */}
                    {section === "showcase" && (
                    <div className="submissions-card">
                        <div className="admin-users-header">
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                                <FileText size={16} color="var(--bronze)" />
                                <p style={{ margin: 0, fontSize: 13, color: "var(--ink-400)" }}>{showcasePapers.length} published</p>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <button
                                    onClick={openShowcaseExport}
                                    disabled={scExporting || showcasePapers.length === 0}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 5,
                                        padding: "6px 14px", borderRadius: 8,
                                        border: "1px solid var(--border)", background: "var(--surface)",
                                        color: "var(--ink-900)", fontSize: 12, fontWeight: 500,
                                        cursor: (scExporting || showcasePapers.length === 0) ? "default" : "pointer",
                                        opacity: (scExporting || showcasePapers.length === 0) ? 0.6 : 1, whiteSpace: "nowrap",
                                    }}
                                >
                                    <FileDown size={13} />
                                    {scExporting ? "Exporting..." : "Export CSV"}
                                </button>
                                <button
                                    onClick={() => { setShowUploadShowcase(true); setShowcaseError(""); setShowcaseFile(null); setShowcaseTitle(""); setShowcaseAuthors(""); }}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 5,
                                        padding: "6px 14px", borderRadius: 8,
                                        border: "none", background: "var(--brand)",
                                        color: "white", cursor: "pointer", fontSize: 12, fontWeight: 500,
                                    }}
                                >
                                    <Upload size={13} />
                                    Upload Showcase
                                </button>
                            </div>
                        </div>

                        {(() => {
                            const scTotalPages = Math.max(1, Math.ceil(showcasePapers.length / SHOWCASE_PAGE_SIZE));
                            const scSafePage = Math.min(showcasePage, scTotalPages);
                            const scPaginated = showcasePapers.slice((scSafePage - 1) * SHOWCASE_PAGE_SIZE, scSafePage * SHOWCASE_PAGE_SIZE);
                            return showcaseLoading ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>Loading...</div>
                        ) : showcasePapers.length === 0 ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>No showcase papers yet.</div>
                        ) : (
                            <>
                            <table className="submissions-table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th className="admin-col-email">Status</th>
                                        <th className="admin-col-joined">Date</th>
                                        <th className="admin-col-actions"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scPaginated.map(m => {
                                        const s = m.status?.toLowerCase();
                                        const isComplete = s === "completed";
                                        const isError = s === "error";
                                        return (
                                            <tr key={m.id} className="submission-row" style={{ cursor: isComplete ? "pointer" : "default" }} onClick={() => isComplete && window.open("/showcase/" + m.showcaseKey, "_blank")}>
                                                <td>
                                                    <span style={{ fontWeight: 500, color: "var(--ink-900)", fontSize: 13 }}>
                                                        {m.title || m.name || "Untitled"}
                                                    </span>
                                                    {m.authors && <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--ink-400)" }}>{m.authors}</p>}
                                                    <div className="showcase-mobile-meta">
                                                        <span className={`status-badge ${isComplete ? "badge-complete" : isError ? "badge-error" : "badge-processing"}`}>
                                                            {isComplete ? <CheckCircle size={12} /> : isError ? <AlertCircle size={12} /> : <Clock3 size={12} />}
                                                            {isComplete ? "Complete" : isError ? "Error" : "Processing"}
                                                        </span>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                            {isComplete && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); copyShowcaseLink(m.showcaseKey); }}
                                                                    style={{
                                                                        background: "none", border: "1px solid var(--border)", borderRadius: 6,
                                                                        padding: "4px 8px", cursor: "pointer", fontSize: 11,
                                                                        color: copiedKey === m.showcaseKey ? "#22c55e" : "var(--ink-500)",
                                                                        display: "flex", alignItems: "center", justifyContent: "center", gap: 4, whiteSpace: "nowrap", width: 85,
                                                                    }}
                                                                >
                                                                    <Link size={12} />
                                                                    {copiedKey === m.showcaseKey ? "Copied" : "Copy Link"}
                                                                </button>
                                                            )}
                                                            {(isComplete || isError) && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleRemoveShowcase(m.id); }}
                                                                    disabled={removingShowcaseId === m.id}
                                                                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-200)", padding: 4, display: "flex", alignItems: "center" }}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="admin-col-email">
                                                    <span className={`status-badge ${isComplete ? "badge-complete" : isError ? "badge-error" : "badge-processing"}`}>
                                                        {isComplete ? <CheckCircle size={12} /> : isError ? <AlertCircle size={12} /> : <Clock3 size={12} />}
                                                        {isComplete ? "Complete" : isError ? "Error" : "Processing"}
                                                    </span>
                                                </td>
                                                <td className="admin-col-joined" style={{ color: "var(--ink-350)", fontSize: 12 }}>{fmt(m.uploadDate)}</td>
                                                <td className="admin-col-actions">
                                                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                                        {isComplete && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); copyShowcaseLink(m.showcaseKey); }}
                                                                style={{
                                                                    background: "none", border: "1px solid var(--border)", borderRadius: 6,
                                                                    padding: "4px 8px", cursor: "pointer", fontSize: 11,
                                                                    color: copiedKey === m.showcaseKey ? "#22c55e" : "var(--ink-500)",
                                                                    display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
                                                                }}
                                                                title="Copy shareable link"
                                                            >
                                                                <Link size={12} />
                                                                {copiedKey === m.showcaseKey ? "Copied" : "Copy Link"}
                                                            </button>
                                                        )}
                                                        {(isComplete || isError) && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleRemoveShowcase(m.id); }}
                                                                disabled={removingShowcaseId === m.id}
                                                                style={{
                                                                    background: "none", border: "none", cursor: "pointer",
                                                                    color: "var(--ink-200)", padding: 4, display: "flex", alignItems: "center",
                                                                }}
                                                                title="Remove showcase"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {scTotalPages > 1 && (
                                <div style={{ borderTop: "1px solid var(--border-subtle)", padding: "12px 20px" }}>
                                    <div className="pagination-bar">
                                        <p className="pagination-count">{(scSafePage - 1) * SHOWCASE_PAGE_SIZE + 1}-{Math.min(scSafePage * SHOWCASE_PAGE_SIZE, showcasePapers.length)} of {showcasePapers.length}</p>
                                        <div className="pagination-controls">
                                            <button onClick={() => setShowcasePage(p => Math.max(1, p - 1))} disabled={scSafePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: scSafePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: scSafePage === 1 ? "default" : "pointer", fontSize: 12, color: scSafePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                                            <div className="pagination-pages">
                                                {pageWindows(scSafePage, scTotalPages).map((n, i) =>
                                                    n === null
                                                        ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>...</span>
                                                        : <button key={n} onClick={() => setShowcasePage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === scSafePage ? "var(--brand)" : "var(--surface)", color: n === scSafePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === scSafePage ? 600 : 400 }}>{n}</button>
                                                )}
                                            </div>
                                            <button onClick={() => setShowcasePage(p => Math.min(scTotalPages, p + 1))} disabled={scSafePage === scTotalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: scSafePage === scTotalPages ? "var(--surface-3)" : "var(--surface)", cursor: scSafePage === scTotalPages ? "default" : "pointer", fontSize: 12, color: scSafePage === scTotalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                                        </div>
                                        <div className="pagination-spacer" />
                                    </div>
                                </div>
                            )}
                            </>
                        );
                        })()}
                    </div>
                    )}
        </AdminLayout>
    );
}
